import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Edit2, Plus, Trash2 } from "lucide-react";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { Header } from "../components/layout/Header";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const API = `${process.env.REACT_APP_BACKEND_URL || "http://localhost:8000"}/api`;
const defaultBankOptions = ["BCA", "BRI", "BNI", "MANDIRI", "SEABANK"];

const initialForm = {
  owner_type: "PT",
  owner_id: "",
  bank_name: "",
  account_name: "",
  account_number: "",
  sort_order: "",
  is_active: true,
};

const ownerOptionLabel = (owner) => {
  const ownerName = String(owner?.name || "-").trim();
  const merchantName = String(owner?.merchant_name || "").trim();
  return merchantName ? `${ownerName} - ${merchantName}` : `${ownerName} - Tanpa Merchant`;
};

export default function MasterBankAccounts() {
  const [accounts, setAccounts] = useState([]);
  const [banks, setBanks] = useState(defaultBankOptions);
  const [ptTrainers, setPtTrainers] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [formData, setFormData] = useState(initialForm);

  const ownerOptions = useMemo(() => {
    const source = formData.owner_type === "COACH" ? coaches : ptTrainers;
    return source
      .sort((a, b) => {
        const nameCompare = String(a.name || "").localeCompare(String(b.name || ""));
        if (nameCompare !== 0) return nameCompare;
        return String(a.merchant_name || "").localeCompare(String(b.merchant_name || ""));
      });
  }, [coaches, formData.owner_type, ptTrainers]);

  const sortedAccounts = useMemo(
    () =>
      accounts.filter((account) => String(account.merchant_id || "").trim()).sort((a, b) => {
        const orderA = Number(a.sort_order || 0);
        const orderB = Number(b.sort_order || 0);
        if (orderA !== orderB) return orderA - orderB;
        return String(a.owner_name || "").localeCompare(String(b.owner_name || ""));
      }),
    [accounts]
  );

  const fetchData = async () => {
    try {
      setLoading(true);
      const [accountsRes, banksRes, ptRes, coachRes] = await Promise.all([
        axios.get(`${API}/bank-accounts`, { withCredentials: true }),
        axios.get(`${API}/banks`, { withCredentials: true }),
        axios.get(`${API}/pt-trainers`, { withCredentials: true }),
        axios.get(`${API}/cardio-coaches`, { withCredentials: true }),
      ]);
      setAccounts(Array.isArray(accountsRes.data) ? accountsRes.data : []);
      const activeBanks = (Array.isArray(banksRes.data) ? banksRes.data : [])
        .filter((item) => item && item.is_active !== false)
        .map((item) => String(item.name || "").trim())
        .filter(Boolean);
      setBanks(activeBanks.length > 0 ? activeBanks : defaultBankOptions);
      setPtTrainers(Array.isArray(ptRes.data) ? ptRes.data : []);
      setCoaches(Array.isArray(coachRes.data) ? coachRes.data : []);
    } catch (error) {
      console.error("Failed to fetch bank accounts:", error);
      alert(error.response?.data?.detail || "Gagal mengambil data master rekening.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openCreate = () => {
    setEditingAccount(null);
    setFormData(initialForm);
    setIsModalOpen(true);
  };

  const openEdit = (account) => {
    setEditingAccount(account);
    setFormData({
      owner_type: String(account.owner_type || "PT"),
      owner_id: String(account.owner_id || ""),
      bank_name: String(account.bank_name || ""),
      account_name: String(account.account_name || ""),
      account_number: String(account.account_number || ""),
      sort_order: String(account.sort_order ?? ""),
      is_active: account.is_active !== false,
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (accountId) => {
    if (!window.confirm("Hapus rekening ini?")) return;
    try {
      await axios.delete(`${API}/bank-accounts/${accountId}`, { withCredentials: true });
      fetchData();
    } catch (error) {
      console.error("Failed to delete bank account:", error);
      alert(error.response?.data?.detail || "Gagal menghapus rekening.");
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const payload = {
      owner_type: formData.owner_type,
      owner_id: formData.owner_id,
      bank_name: String(formData.bank_name || "").trim().toUpperCase(),
      account_name: String(formData.account_name || "").trim(),
      account_number: String(formData.account_number || "").trim(),
      sort_order: Number(formData.sort_order || 0),
      is_active: !!formData.is_active,
    };
    if (!payload.owner_id || !payload.bank_name || !payload.account_name || !payload.account_number) {
      alert("Pemilik rekening, bank, account name, dan nomor rekening wajib diisi.");
      return;
    }

    try {
      if (editingAccount) {
        await axios.put(`${API}/bank-accounts/${editingAccount.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/bank-accounts`, payload, { withCredentials: true });
      }
      setIsModalOpen(false);
      setEditingAccount(null);
      setFormData(initialForm);
      fetchData();
    } catch (error) {
      console.error("Failed to save bank account:", error);
      alert(error.response?.data?.detail || "Gagal menyimpan rekening.");
    }
  };

  return (
    <DashboardLayout>
      <Header title="Master Rekening" />
      <div className="space-y-6 p-8" data-testid="master-rekening-page">
        <section className="rounded-sm border border-white/10 bg-zinc-900/50 p-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-xl font-semibold text-white">Daftar Rekening PT & Coach</h2>
              <p className="mt-1 text-sm text-zinc-500">Kelola rekening yang tersinkron ke Master PT dan Master Coach.</p>
            </div>
            <Button onClick={openCreate} className="rounded-sm bg-[#D4AF37] font-semibold text-black hover:bg-[#C59A27]">
              <Plus className="mr-2 h-4 w-4" />
              Tambah Rekening
            </Button>
          </div>
        </section>

        <section className="overflow-hidden rounded-sm border border-white/10 bg-zinc-900/50">
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead>No</TableHead>
                <TableHead>Pemilik</TableHead>
                <TableHead>Bank</TableHead>
                <TableHead>Account Name</TableHead>
                <TableHead>No Rekening</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="py-8 text-center text-sm text-zinc-500">Loading...</TableCell>
                </TableRow>
              ) : sortedAccounts.length > 0 ? (
                sortedAccounts.map((account, index) => (
                  <TableRow key={account.id || index} className="border-white/5 hover:bg-zinc-900/50">
                    <TableCell className="py-4 text-sm text-zinc-300">{index + 1}</TableCell>
                    <TableCell className="py-4">
                      <p className="text-sm font-semibold text-white">
                        {account.owner_name || account.owner_id}
                        {account.merchant_name ? <span className="ml-2 text-xs font-medium text-[#D4AF37]">- {account.merchant_name}</span> : null}
                      </p>
                      <p className="mt-1 text-xs text-zinc-500">{account.owner_type === "COACH" ? "Master Coach" : "Master PT"}</p>
                    </TableCell>
                    <TableCell className="py-4 text-sm text-zinc-300">{account.bank_name || "-"}</TableCell>
                    <TableCell className="py-4 text-sm font-medium text-zinc-100">{account.account_name || "-"}</TableCell>
                    <TableCell className="py-4 text-sm text-zinc-300">{account.account_number || "-"}</TableCell>
                    <TableCell className="py-4">
                      <Badge
                        variant="outline"
                        className={`rounded-sm text-xs ${
                          account.is_active !== false
                            ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-400"
                            : "border-zinc-500/30 bg-zinc-500/10 text-zinc-400"
                        }`}
                      >
                        {account.is_active !== false ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell className="py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => openEdit(account)}
                          className="rounded-sm p-2 text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-white"
                        >
                          <Edit2 className="h-4 w-4" strokeWidth={1.5} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(account.id)}
                          className="rounded-sm p-2 text-zinc-400 transition-colors hover:bg-red-500/10 hover:text-red-400"
                        >
                          <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} className="py-8 text-center text-sm text-zinc-500">
                    Belum ada data rekening.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </section>

        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogContent className="max-w-2xl border-white/10 bg-zinc-900 text-white">
            <DialogHeader>
              <DialogTitle>{editingAccount ? "Edit Rekening" : "Tambah Rekening"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="mt-4 space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Tipe Pemilik</Label>
                  <Select
                    value={formData.owner_type}
                    onValueChange={(value) => setFormData((prev) => ({ ...prev, owner_type: value, owner_id: "" }))}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PT">Master PT</SelectItem>
                      <SelectItem value="COACH">Master Coach</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Nama Pemilik</Label>
                  <Select
                    value={formData.owner_id || undefined}
                    onValueChange={(value) => setFormData((prev) => ({ ...prev, owner_id: value }))}
                  >
                    <SelectTrigger><SelectValue placeholder="Pilih pemilik" /></SelectTrigger>
                    <SelectContent>
                      {ownerOptions.map((owner) => (
                        <SelectItem key={owner.id} value={owner.id}>{ownerOptionLabel(owner)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Bank</Label>
                  <Select
                    value={formData.bank_name || undefined}
                    onValueChange={(value) => setFormData((prev) => ({ ...prev, bank_name: value }))}
                  >
                    <SelectTrigger><SelectValue placeholder="Pilih bank" /></SelectTrigger>
                    <SelectContent>
                      {[...new Set([formData.bank_name, ...banks].filter(Boolean))].map((bank) => (
                        <SelectItem key={bank} value={bank}>{bank}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Account Name</Label>
                  <Input
                    value={formData.account_name}
                    onChange={(e) => setFormData((prev) => ({ ...prev, account_name: e.target.value }))}
                    placeholder="Nama sesuai rekening"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>No Rekening</Label>
                  <Input
                    value={formData.account_number}
                    onChange={(e) => setFormData((prev) => ({ ...prev, account_number: e.target.value }))}
                    placeholder="Nomor rekening"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Sort Order</Label>
                  <Input
                    type="number"
                    value={formData.sort_order}
                    onChange={(e) => setFormData((prev) => ({ ...prev, sort_order: e.target.value }))}
                    placeholder="0"
                  />
                </div>
              </div>
              <div className="flex items-center justify-between rounded-sm border border-white/10 bg-black/20 p-3">
                <div>
                  <Label>Active</Label>
                  <p className="mt-1 text-xs text-zinc-500">Rekening active akan tersinkron ke Master PT/Coach.</p>
                </div>
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, is_active: checked }))}
                />
              </div>
              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} className="flex-1 rounded-sm border-white/10 text-white hover:bg-zinc-800">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 rounded-sm bg-[#D4AF37] font-semibold text-black hover:bg-[#C59A27]">
                  {editingAccount ? "Update Rekening" : "Simpan Rekening"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
